import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Building2, LogIn, ArrowRight, Key, ArrowLeft, Mail, Lock, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login({ initialStep = 'login' }) {
  const { login, selectCompany, companies: authCompanies, token, user, changePassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(initialStep);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [loginError, setLoginError] = useState('');
  
  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Force password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [forceChangePassword, setForceChangePassword] = useState(false);

  // Check if setup is required on mount
  useEffect(() => {
    const checkSetup = async () => {
      try {
        const res = await api.get('/setup/check');
        if (res.data.setup_required) {
          navigate('/setup');
          return;
        }
      } catch (err) {
        console.error('Setup check failed:', err);
      }
      setCheckingSetup(false);
    };
    checkSetup();
  }, [navigate]);

  // Check for reset token in URL
  useEffect(() => {
    const urlToken = searchParams.get('reset_token');
    if (urlToken) {
      setResetToken(urlToken);
      setStep('reset-password');
    }
  }, [searchParams]);

  // Sync companies from auth context when available (for /select-company route)
  useEffect(() => {
    if (step === 'company' && companies.length === 0 && authCompanies.length > 0) {
      setCompanies(authCompanies);
    }
  }, [step, companies.length, authCompanies]);

  // If initialStep changes (route change), update step
  useEffect(() => {
    setStep(initialStep);
  }, [initialStep]);

  // Show loading while checking setup
  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f5f0e8] to-[#e8dfd0] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#2e2d29]" size={32} />
      </div>
    );
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      
      // Check if temporary password
      if (result.user?.is_temporary_password) {
        setForceChangePassword(true);
        setCurrentPassword(password);
        setStep('force-change-password');
        setLoading(false);
        return;
      }
      
      // Otomatik şirket seçimi yapıldıysa direkt yönlendir
      if (result.autoSelectedCompany) {
        const redirect = sessionStorage.getItem('redirect_after_login') || '/erp';
        sessionStorage.removeItem('redirect_after_login');
        navigate(redirect, { replace: true });
        return;
      }
      setCompanies(result.companies);
      setStep('company');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Giriş başarısız';
      setLoginError(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = async (companyId) => {
    setLoading(true);
    try {
      await selectCompany(companyId);
      // Logout öncesi konuma dön, yoksa kontrol paneline git
      const redirect = sessionStorage.getItem('redirect_after_login') || '/erp';
      sessionStorage.removeItem('redirect_after_login');
      navigate(redirect, { replace: true });
    } catch (err) {
      toast.error('Şirket seçimi başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: forgotEmail });
      toast.success('Eğer email kayıtlıysa sıfırlama linki gönderildi');
      // For development, show the token
      if (res.data.debug_token) {
        console.log('Reset token:', res.data.debug_token);
        toast.info(`Geliştirme modu: Token=${res.data.debug_token.slice(0, 10)}...`);
      }
      setStep('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token: resetToken, new_password: newPassword });
      toast.success('Şifre başarıyla sıfırlandı');
      setStep('login');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleForceChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }
    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Şifre değiştirildi');
      setStep('company');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f0e8] via-[#ede5d8] to-[#e8dfd0] flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-3">
            <div className="w-11 h-11 bg-[#2e2d29] rounded-xl flex items-center justify-center shadow-sm">
              <span className="text-[#f5f0e8] font-extrabold text-lg">T</span>
            </div>
            <h1 className="text-3xl font-extrabold text-[#1a1916] tracking-tight">TorosERP</h1>
          </div>
          <p className="text-[#8a7e70] text-sm">Kurumsal Kaynak Planlama Sistemi</p>
        </div>

        {/* Login Step */}
        {step === 'login' && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-md" data-testid="login-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#1a1916]">Giriş Yap</CardTitle>
              <CardDescription className="text-[#8a7e70]">Hesabınıza giriş yapın</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#3F3E39] font-medium">E-posta</Label>
                  <Input
                    id="email" type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@sirket.com"
                    className="bg-white border-[#d0c4b0] focus:ring-[#2e2d29] focus:border-[#2e2d29]"
                    data-testid="login-email-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#3F3E39] font-medium">Şifre</Label>
                  <Input
                    id="password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white border-[#d0c4b0] focus:ring-[#2e2d29] focus:border-[#2e2d29]"
                    data-testid="login-password-input"
                    required
                  />
                </div>
                {loginError && (
                  <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                    <span>{loginError}</span>
                  </div>
                )}
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                  <LogIn size={16} className="ml-2" />
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button 
                  onClick={() => setStep('forgot-password')}
                  className="text-sm text-[#8a7e70] hover:text-[#1a1916] underline"
                  data-testid="forgot-password-link"
                >
                  Şifremi Unuttum
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Forgot Password Step */}
        {step === 'forgot-password' && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm" data-testid="forgot-password-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#1a1916] flex items-center gap-2">
                <Mail size={20} /> Şifremi Unuttum
              </CardTitle>
              <CardDescription className="text-[#8a7e70]">
                E-posta adresinizi girin, şifre sıfırlama linki göndereceğiz.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-[#3F3E39] font-medium">E-posta</Label>
                  <Input
                    id="forgot-email" type="email" value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="email@sirket.com"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="forgot-email-input"
                    required
                  />
                </div>
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] font-semibold"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Gönderiliyor...' : 'Sıfırlama Linki Gönder'}
                </Button>
              </form>
              <div className="mt-4 text-center">
                <button 
                  onClick={() => setStep('login')}
                  className="text-sm text-[#8a7e70] hover:text-[#1a1916] flex items-center justify-center gap-1"
                >
                  <ArrowLeft size={14} /> Giriş sayfasına dön
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reset Password Step */}
        {step === 'reset-password' && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm" data-testid="reset-password-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#1a1916] flex items-center gap-2">
                <Key size={20} /> Yeni Şifre Belirle
              </CardTitle>
              <CardDescription className="text-[#8a7e70]">
                Yeni şifrenizi girin.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Yeni Şifre</Label>
                  <Input
                    type="password" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="new-password-input"
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Şifre Tekrar</Label>
                  <Input
                    type="password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="confirm-password-input"
                    required
                  />
                </div>
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] font-semibold"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Kaydediliyor...' : 'Şifreyi Değiştir'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Force Change Password Step */}
        {step === 'force-change-password' && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm" data-testid="force-change-password-card">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2 text-yellow-600 mb-2">
                <AlertTriangle size={20} />
                <span className="font-medium">Şifre Değişikliği Gerekli</span>
              </div>
              <CardTitle className="text-xl font-bold text-[#1a1916]">Yeni Şifre Belirleyin</CardTitle>
              <CardDescription className="text-[#8a7e70]">
                Güvenliğiniz için geçici şifrenizi değiştirmeniz gerekmektedir.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForceChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Yeni Şifre</Label>
                  <Input
                    type="password" value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="force-new-password-input"
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#3F3E39] font-medium">Şifre Tekrar</Label>
                  <Input
                    type="password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white border-[#d0c4b0]"
                    data-testid="force-confirm-password-input"
                    required
                  />
                </div>
                <Button
                  type="submit" disabled={loading}
                  className="w-full bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] font-semibold"
                  data-testid="force-change-submit-btn"
                >
                  {loading ? 'Kaydediliyor...' : 'Şifreyi Değiştir ve Devam Et'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Company Select Step */}
        {step === 'company' && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm" data-testid="company-select-card">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#1a1916]">Şirket Seçin</CardTitle>
              <CardDescription className="text-[#8a7e70]">Çalışma yapmak istediğiniz şirketi seçin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {companies.length === 0 ? (
                <p className="text-center py-4 text-[#8a7e70]" data-testid="loading-companies">
                  Şirketler yükleniyor...
                </p>
              ) : (
                companies.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleCompanySelect(c.id)}
                    disabled={loading}
                    data-testid={`company-select-${c.id}`}
                    className="w-full flex items-center gap-4 p-4 rounded-lg border border-[#d0c4b0] bg-white hover:bg-[#e8dfd0]/50 hover:border-[#8a7e70] transition-all duration-150 text-left group"
                  >
                    <div className="w-10 h-10 rounded-md bg-[#e8dfd0] flex items-center justify-center">
                      <Building2 size={20} className="text-[#3F3E39]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1a1916]">{c.name}</p>
                      <p className="text-xs text-[#8a7e70]">{c.tax_number}</p>
                    </div>
                    <ArrowRight size={16} className="text-[#8a7e70] group-hover:text-[#1a1916] transition-colors" />
                  </button>
                ))
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
